import { divLog } from 'apmui/util/divLog';

/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/// <reference types="edgeclient" />

enum DEFAULT {
    // Session
    WebLogonClearSession = 'WebLogonClearSession',
    WebLogonResetSession = 'WebLogonResetSession',
    // Soft Token
    WebLogonSoftTokenSupport = 'WebLogonSoftTokenSupport',
    CanRequestWeblogonSoftTokenPIN = 'CanRequestWeblogonSoftTokenPIN',

    WebLogonSoftTokenPrompt = 'WebLogonSoftTokenPrompt',
    WebLogonSoftTokenChallengeResponse = 'WebLogonSoftTokenChallengeResponse',
    // Username, Password
    WebLogonUserName = 'WebLogonUserName',
    WebLogonPassword = 'WebLogonPassword',

    WebLogonSavePasswordAvailable = 'WebLogonSavePasswordAvailable',
    WebLogonSavePasswordChecked = 'WebLogonSavePasswordChecked',
    // Autologon
    WebLogonAutoLogon = 'WebLogonAutoLogon',
    WebLogonOnAuthFailure = 'WebLogonOnAuthFailure',
    // Callback
    WebLogonAutosubmitCall = 'WebLogonAutosubmitCall',
    WebLogonChallengeAutosubmitCall = 'WebLogonChallengeAutosubmitCall',
    //
    OnHostClose = 'OnHostClose',
    WebLogonNotifyUser = 'WebLogonNotifyUser',
    DebugLog = 'DebugLog',
}

enum ANDROID {
    // Session
    webLogonClearSession = 'webLogonClearSession',
    webLogonResetSession = 'webLogonResetSession',
    // Soft Token
    hasWebLogonSoftTokenSupport = 'hasWebLogonSoftTokenSupport',
    canRequestWeblogonSoftTokenPIN = 'canRequestWeblogonSoftTokenPIN',

    getWebLogonSoftTokenError = 'getWebLogonSoftTokenError',

    setWebLogonSoftTokenPrompt = 'setWebLogonSoftTokenPrompt',
    getWeblogonSoftTokenChallengeResponse = 'getWeblogonSoftTokenChallengeResponse',
    // Username, Password
    getWebLogonUserName = 'getWebLogonUserName',
    setWebLogonUserName = 'setWebLogonUserName',

    getWebLogonPassword = 'getWebLogonPassword',
    setWebLogonPassword = 'setWebLogonPassword',

    setWebLogonSavePasswordChecked = 'setWebLogonSavePasswordChecked',
    getWebLogonSavePasswordChecked = 'getWebLogonSavePasswordChecked',
    // Autologon
    isWebLogonSavePasswordAvailable = 'isWebLogonSavePasswordAvailable',
    getWebLogonAutoLogon = 'getWebLogonAutoLogon',
    setWebLogonAutoLogon = 'setWebLogonAutoLogon',
    // Callback
    setWeblogonCallbacks = 'setWeblogonCallbacks',
    //
    webLogonNotifyUser = 'webLogonNotifyUser',
    //
    debugLog = 'debugLog',
}

interface IClient {
    available(): boolean;
    // Session
    hasClearSession(): this is {
        clearSession(): void;
    };
    hasResetSession(): this is {
        resetSession(): void;
    };
    // Soft Token
    hasSoftTokenSupport(): this is {
        setSoftTokenState(state: string): void;
        setSoftTokenPIN(state: string): void;
        getSoftTokenPasscode(): string;
        getSoftTokenError(): string;
    };
    canRequestSoftTokenPIN(): boolean;
    hasSetSoftTokenPrompt(): this is {
        setSoftTokenPrompt(prompt: string): void;
    };
    hasSoftTokenChallengeResponse(): this is {
        getSoftTokenChallengeResponse(): string;
    };
    // Username, Password
    hasUsername(): this is {
        getUsername(): string;
        setUsername(username: string): void;
    };
    hasPassword(): this is {
        getPassword(): string;
        setPassword(password: string): void;
    };
    // Save password
    isSavePasswordAvailable(): boolean;
    hasSavePasswordChecked(): this is {
        getSavePasswordChecked(): boolean;
        setSavePasswordChecked(checked: boolean): void;
    };
    // Autologon, Autosubmit
    hasAutoLogon(): this is {
        getAutoLogon(): boolean;
        setAutoLogon(autoLogon: boolean): void;
    };
    //
    hasOnAuthFailure(): this is {
        onAuthFailure(): void;
    };
    // Callbacks
    setCallbacks(autosubmitFunction: string, challengeAutosubmitFunction: string): void;
    //
    hasOnHostClose(): this is {
        onHostClose(): void;
    };
    // Notify
    hasNotifyUser(): this is {
        notifyUser(): void;
    };
    //
    hasDebugLog(): this is {
        debugLog(message: string): void;
    };
}

const logThru = <T, U>(name: T, thru: U) => {
    divLog(`edgeClient.${name}() = ${thru}`);
    return thru;
};
const hasProp = (prop: DEFAULT) => typeof window.external == 'object' && prop in window.external;

const defaultClient = {
    available: () => logThru('available', Boolean(typeof window.external == 'object' && window.external)),
    // Session
    hasClearSession: () => logThru('hasClearSession', hasProp(DEFAULT.WebLogonClearSession)),
    clearSession: () => window.external.WebLogonClearSession(),

    hasResetSession: () => logThru('hasResetSession', hasProp(DEFAULT.WebLogonResetSession)),
    resetSession: () => window.external.WebLogonResetSession(),
    // RSA Soft Token
    hasSoftTokenSupport: () => logThru('hasSoftTokenSupport', hasProp(DEFAULT.WebLogonSoftTokenSupport)),
    setSoftTokenState: (state: string) => (window.external.WebLogonSoftTokenState = state),
    setSoftTokenPIN: (pin: string) => (window.external.WebLogonSoftTokenPIN = pin),
    getSoftTokenPasscode: () => window.external.WebLogonSoftTokenPasscode,
    getSoftTokenError: () => window.external.WebLogonSoftTokenError,

    canRequestSoftTokenPIN: () =>
        logThru(
            'canRequestSoftTokenPIN',
            Boolean(hasProp(DEFAULT.CanRequestWeblogonSoftTokenPIN) && window.external.CanRequestWeblogonSoftTokenPIN)
        ),

    hasSetSoftTokenPrompt: () => logThru('hasSetSoftTokenPrompt', hasProp(DEFAULT.WebLogonSoftTokenPrompt)),
    setSoftTokenPrompt: (prompt: string) => (window.external.WebLogonSoftTokenPrompt = prompt),

    hasSoftTokenChallengeResponse: () =>
        logThru('hasSoftTokenChallengeResponse', hasProp(DEFAULT.WebLogonSoftTokenChallengeResponse)),
    getSoftTokenChallengeResponse: () => window.external.WebLogonSoftTokenChallengeResponse,

    // Username, Password
    hasUsername: () => logThru('hasUsername', hasProp(DEFAULT.WebLogonUserName)),
    getUsername: () => window.external.WebLogonUserName,
    setUsername: (username: string) => (window.external.WebLogonUserName = username),

    hasPassword: () => logThru('hasPassword', hasProp(DEFAULT.WebLogonPassword)),
    getPassword: () => window.external.WebLogonPassword,
    setPassword: (password: string) => (window.external.WebLogonPassword = password),
    // Save password
    isSavePasswordAvailable: () =>
        logThru('', hasProp(DEFAULT.WebLogonSavePasswordAvailable) && window.external.WebLogonSavePasswordAvailable),

    hasSavePasswordChecked: () => logThru('hasSavePasswordChecked', hasProp(DEFAULT.WebLogonSavePasswordChecked)),
    getSavePasswordChecked: () => window.external.WebLogonSavePasswordChecked,
    setSavePasswordChecked: (checked: boolean) => (window.external.WebLogonSavePasswordChecked = checked),
    // Autologon
    hasAutoLogon: () => logThru('hasAutoLogon', hasProp(DEFAULT.WebLogonAutoLogon)),
    getAutoLogon: () => window.external.WebLogonAutoLogon,
    setWebLogonAutoLogon: (autoLogon: boolean) => (window.external.WebLogonAutoLogon = autoLogon),
    // Auth failure
    hasOnAuthFailure: () => logThru('hasOnAuthFailure', hasProp(DEFAULT.WebLogonOnAuthFailure)),
    onAuthFailure: () => window.external.WebLogonOnAuthFailure(),
    // Callbacks
    setCallbacks: (autosubmitFunction: string, challengeAutosubmitFunction: string) => {
        if (hasProp(DEFAULT.WebLogonAutosubmitCall)) {
            window.external.WebLogonAutosubmitCall == autosubmitFunction;
        }
        if (hasProp(DEFAULT.WebLogonChallengeAutosubmitCall)) {
            window.external.WebLogonChallengeAutosubmitCall == challengeAutosubmitFunction;
        }
    },
    //
    hasOnHostClose: () => logThru('hasOnHostClose', hasProp(DEFAULT.OnHostClose)),
    onHostClose: () => window.external.OnHostClose(),
    // Notify
    hasNotifyUser: () => logThru('hasNotifyUser', hasProp(DEFAULT.WebLogonNotifyUser)),
    notifyUser: () => window.external.WebLogonNotifyUser(),
    //
    hasDebugLog: () => logThru('hasDebugLog', hasProp(DEFAULT.DebugLog)),
    debugLog: (message: string) => window.external.DebugLog(message),
};

const isF = (prop: ANDROID) =>
    window.externalAndroidWebHost && typeof window.externalAndroidWebHost[prop] === 'function';
const androidClient = {
    available: () => logThru('available', Boolean(typeof window.external == 'object' && window.external)),

    hasClearSession: () => logThru('hasClearSession', isF(ANDROID.webLogonClearSession)),
    clearSession: () => window.externalAndroidWebHost.webLogonClearSession(),

    hasResetSession: () => logThru('hasResetSession', isF(ANDROID.webLogonResetSession)),
    resetSession: () => window.externalAndroidWebHost.webLogonResetSession(),

    hasSoftTokenSupport: () =>
        logThru(
            'hasSoftTokenSupport',
            isF(ANDROID.hasWebLogonSoftTokenSupport) && window.externalAndroidWebHost.hasWebLogonSoftTokenSupport()
        ),
    setSoftTokenState: (state: string) => window.externalAndroidWebHost.setWebLogonSoftTokenState(state),
    setSoftTokenPIN: (pin: string) => window.externalAndroidWebHost.setWebLogonSoftTokenPIN(pin),
    getSoftTokenPasscode: () => window.externalAndroidWebHost.getWebLogonSoftTokenPasscode(),
    getSoftTokenError: () => window.externalAndroidWebHost.getWebLogonSoftTokenError(),
    canRequestSoftTokenPIN: () => window.externalAndroidWebHost.canRequestWeblogonSoftTokenPIN(),

    hasSetSoftTokenPrompt: () => logThru('hasSetSoftTokenPrompt', isF(ANDROID.setWebLogonSoftTokenPrompt)),
    setSoftTokenPrompt: (prompt: string) => {},

    hasSoftTokenChallengeResponse: () => logThru('hasSoftTokenChallengeResponse', false),
    getSoftTokenChallengeResponse: () => window.externalAndroidWebHost.getWeblogonSoftTokenChallengeResponse,

    hasUsername: () => logThru('hasUsername', isF(ANDROID.getWebLogonUserName)),
    getUsername: () => window.externalAndroidWebHost.getWebLogonUserName(),
    setUsername: (username: string) => window.externalAndroidWebHost.setWebLogonUserName(username),

    hasPassword: () => logThru('hasPassword', isF(ANDROID.getWebLogonPassword)),
    getPassword: () => window.externalAndroidWebHost.getWebLogonPassword(),
    setPassword: (password: string) => window.externalAndroidWebHost.setWebLogonPassword(password),

    isSavePasswordAvailable: () =>
        logThru(
            '',
            isF(ANDROID.isWebLogonSavePasswordAvailable) &&
                window.externalAndroidWebHost.isWebLogonSavePasswordAvailable()
        ),

    hasSavePasswordChecked: () => logThru('hasSavePasswordChecked', isF(ANDROID.setWebLogonSavePasswordChecked)),
    getSavePasswordChecked: () => window.externalAndroidWebHost.getWebLogonSavePasswordChecked,
    setSavePasswordChecked: (checked: boolean) => window.externalAndroidWebHost.setWebLogonSavePasswordChecked(checked),

    hasAutoLogon: () => logThru('hasAutoLogon', isF(ANDROID.getWebLogonAutoLogon)),
    getAutoLogon: () => window.externalAndroidWebHost.getWebLogonAutoLogon,
    setAutoLogon: (autologon: boolean) => window.externalAndroidWebHost.setWebLogonAutoLogon(autologon),
    //
    hasOnAuthFailure: () => logThru('hasOnAuthFailure', false),
    //
    setCallbacks: (autosumbitFunction: string, challengeAutosubmitFunction: string) => {
        if (isF(ANDROID.setWeblogonCallbacks)) {
            window.externalAndroidWebHost.setWeblogonCallbacks(autosumbitFunction, challengeAutosubmitFunction);
        }
    },
    //
    hasOnHostClose: () => logThru('', false),
    //
    hasNotifyUser: () => logThru('hasNotifyUser', isF(ANDROID.webLogonNotifyUser)),
    NotifyUser: () => window.externalAndroidWebHost.webLogonNotifyUser(),
    //
    hasDebugLog: () => logThru('hasDebugLog', isF(ANDROID.debugLog)),
    debugLog: (message: string) => window.externalAndroidWebHost.debugLog(message),
};

const iOSExternalClient = {
    available: () => logThru('available', false),

    hasClearSession: () => false,

    hasResetSession: () => logThru('hasResetSession', false),
    resetSession: () => {},

    hasSoftTokenSupport: () => logThru('hasSoftTokenSupport', false),
    setSoftTokenState: (state: string) => {},
    setSoftTokenPIN: (pin: string) => {},
    getSoftTokenPasscode: () => '',
    getSoftTokenError: () => '',
    canRequestSoftTokenPIN: () => logThru('canRequestSoftTokenPIN', false),

    hasSetSoftTokenPrompt: () => logThru('hasSetSoftTokenPrompt', false),
    setSoftTokenPrompt: (prompt: string) => {},

    hasSoftTokenChallengeResponse: () => logThru('hasSoftTokenChallengeResponse', false),
    getSoftTokenChallengeResponse: () => '',

    // Username, Password
    hasUsername: () => logThru('hasUsername', false),
    getUsername: () => '',
    setUsername: (username: string) => '',

    hasPassword: () => logThru('hasPassword', false),
    getPassword: () => '',
    setPassword: (password: string) => {},
    // Save password
    isSavePasswordAvailable: () => false,

    hasSavePasswordChecked: () => logThru('hasSavePasswordChecked', false),
    getSavePasswordChecked: false,
    setSavePasswordChecked: (checked: boolean) => {},
    // Autologon
    hasAutoLogon: () => logThru('hasAutoLogon', false),
    getAutoLogon: () => false,
    setWebLogonAutoLogon: (autoLogon: boolean) => {},
    //
    hasOnAuthFailure: () => logThru('hasOnAuthFailure', false),
    // Callbacks
    setCallbacks: (autosubmitFunction: string, challengeAutosubmitFunction: string) => {},
    //
    hasOnHostClose: () => logThru('hasOnHostClose', false),
    // Notify
    hasNotifyUser: () => logThru('hasNotifyUser', false),
    notifyUser: () => {},
    //
    hasDebugLog: () => logThru('hasDebugLog', false),
    debugLog: (message: string) => {},
};

const edgeClient = (): IClient => {
    try {
        const userAgent = navigator.userAgent || navigator.vendor;
        if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i) || userAgent.match(/iPod/i)) {
            // TODO: would this api actually work?
            if (
                navigator.userAgent.match(/F5Access/i) ||
                navigator.userAgent.match(/EdgeClient/i) ||
                navigator.userAgent.match(/EdgePortal/i)
            ) {
                const iframe = document.createElement('IFRAME');
                const src = 'f5edgeclient://initialize';
                iframe.setAttribute('src', src);
                document.documentElement.appendChild(iframe);
                iframe.parentNode!.removeChild(iframe);
                (iframe as any) = null;
            }

            return iOSExternalClient;
        } else if (userAgent.match(/Android/i)) {
            return androidClient;
        }
    } catch (e) {
        /* ignore */
    }
    return defaultClient;
};

export { edgeClient as default };
